/**
 * `useLanguage` — read the active `LanguagePort` from the composition-root
 * `PortsContext` (T015).
 *
 * Thin wrapper around `useContext(PortsContext)` that raises a clear error
 * when called outside a `<PortsProvider>` (or when the provider mounted
 * without a `language` port, which in Cluster 1 is disallowed — `language`
 * is a required slot on the `Ports` interface). The error message names
 * the port so the `PortErrorBoundary` scaffolded in T022 can surface it in
 * diagnostics once Cluster 2+ wraps routes; until then, the throw still
 * yields an actionable dev-time message rather than the silent `null` that
 * `createContext<Ports | null>(null)` would otherwise return.
 *
 * Consumers get the full port shape (`active`, `supported`, `set`,
 * `subscribe`). For read-only call sites that only need the active locale,
 * destructure: `const { active } = useLanguage();`.
 */

import { useContext } from 'react';

import { PortsContext } from '../providers/PortsContext.js';
import type { LanguagePort } from './LanguagePort.js';

export function useLanguage(): LanguagePort {
  const ports = useContext(PortsContext);
  if (ports === null) {
    throw new Error(
      'useLanguage must be used within a PortsProvider that provides the language port',
    );
  }
  return ports.language;
}
